import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  styled
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Help,
  AttachFile,
  History,
  AccountBalance
} from '@mui/icons-material';
import { formatDate, formatCurrency } from '../../utils/formatters.js';
import { FundManagementService } from '../../services/FundManagementService';
import { ComplianceChecker } from '../Compliance/ComplianceChecker';
import { DocumentViewer } from '../Documents/DocumentViewer';

interface PaymentRequest {
  id: string;
  requestDate: Date;
  vendor: {
    id: string;
    name: string;
    w9Status: 'verified' | 'pending' | 'expired' | 'missing';
    ofacStatus: 'clear' | 'review' | 'blocked';
  };
  amount: number;
  description: string;
  category: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'needs_info';
  documents: Array<{
    id: string;
    type: string;
    name: string;
    url: string;
    verified: boolean;
  }>;
  budget: {
    lineItem: string;
    remaining: number;
    totalAllocated: number;
  };
  compliance: {
    checks: Array<{
      type: string;
      status: 'pass' | 'fail' | 'warning';
      message: string;
    }>;
    restrictions: Array<{
      type: string;
      description: string;
      impact: 'blocked' | 'warning' | 'none';
    }>;
  };
  approvals: Array<{
    level: string;
    approver: string;
    date: Date;
    notes: string;
  }>;
  history: Array<{
    date: Date;
    action: string;
    user: string;
    notes: string;
  }>;
  fundName: string;
}

interface PaymentRequestQueueProps {
  fundId: string | 'all';
  onApprove: (requestId: string) => Promise<void>;
  onReject: (requestId: string, reason: string) => Promise<void>;
  onRequestInfo: (requestId: string, info: string) => Promise<void>;
}

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: '#0A1929',
  borderRadius: '12px',
  border: '1px solid rgba(82,153,255,0.1)',
  boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: 'rgba(255,255,255,0.85)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  padding: '16px',
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  color: 'rgba(255,255,255,0.95)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  background: 'linear-gradient(90deg, rgba(41,66,112,0.3) 0%, rgba(28,103,88,0.3) 100%)',
  fontWeight: 600,
  padding: '16px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: 'rgba(82,153,255,0.05)',
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #2962FF 0%, #34D399 100%)',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(90deg, #1E4BD8 0%, #2AAC7D 100%)',
  },
}));

export const PaymentRequestQueue: React.FC<PaymentRequestQueueProps> = ({
  fundId,
  onApprove,
  onReject,
  onRequestInfo
}) => {
  const [requests, setRequests] = useState<PaymentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<PaymentRequest | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);

  const fundService = new FundManagementService();

  const loadPaymentRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let paymentRequests;
      if (fundId === 'all') {
        paymentRequests = await fundService.getAllPaymentRequests();
      } else {
        paymentRequests = await fundService.getFundPaymentRequests(fundId);
      }
      
      setRequests(paymentRequests);
    } catch (error) {
      console.error('Failed to load payment requests:', error);
      setError('Failed to load payment requests');
    } finally {
      setLoading(false);
    }
  }, [fundId, fundService]);

  useEffect(() => {
    loadPaymentRequests();
  }, [loadPaymentRequests]);

  const handleApprove = async (request: PaymentRequest) => {
    try {
      const complianceResult = await fundService.validatePaymentCompliance(request.id);
      if (!complianceResult.valid) {
        setError(complianceResult.errors.join('\n'));
        return;
      }

      if (request.amount > request.budget.remaining) {
        setError('Insufficient budget for this payment');
        return;
      }

      await onApprove(request.id);
      await loadPaymentRequests();
      setDetailsOpen(false);
    } catch (err) {
      setError('Failed to approve payment request');
      console.error(err);
    }
  };

  const handleReject = async () => {
    try {
      if (!selectedRequest) return;
      await onReject(selectedRequest.id, rejectReason);
      await loadPaymentRequests();
      setRejectDialogOpen(false);
      setDetailsOpen(false);
    } catch (err) {
      setError('Failed to reject payment request');
      console.error(err);
    }
  };

  const handleRequestInfo = async () => {
    try {
      if (!selectedRequest) return;
      await onRequestInfo(selectedRequest.id, additionalInfo);
      await loadPaymentRequests();
      setInfoDialogOpen(false);
      setDetailsOpen(false);
    } catch (err) {
      setError('Failed to request additional information');
      console.error(err);
    }
  };

  const getStatusChip = (status: string): { label: string; color: string } => {
    const statusConfig: Record<string, { color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning', label: string }> = {
      pending: { color: 'warning', label: 'Pending Review' },
      under_review: { color: 'info', label: 'Under Review' },
      approved: { color: 'success', label: 'Approved' },
      rejected: { color: 'error', label: 'Rejected' },
      needs_info: { color: 'secondary', label: 'Needs Information' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return {
      label: config.label,
      color: config.color
    };
  };

  if (!fundId) {
    return (
      <StyledPaper sx={{ p: 4, textAlign: 'center' }}>
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'rgba(255,255,255,0.7)',
            background: 'linear-gradient(90deg, #2962FF 0%, #34D399 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          Select a Fund to View Payment Requests
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'rgba(255,255,255,0.5)',
            mt: 2 
          }}
        >
          Choose a fund from the dropdown above to manage its payment requests
        </Typography>
      </StyledPaper>
    );
  }

  if (loading) {
    return (
      <StyledPaper sx={{ p: 4, textAlign: 'center' }}>
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'rgba(255,255,255,0.7)',
            background: 'linear-gradient(90deg, #2962FF 0%, #34D399 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          Loading payment requests...
        </Typography>
      </StyledPaper>
    );
  }
  
  if (error) return (
    <Alert 
      severity="error" 
      sx={{ 
        background: 'rgba(211,47,47,0.1)', 
        color: '#ff8a80',
        border: '1px solid rgba(211,47,47,0.2)'
      }}
    >
      {error}
    </Alert>
  );

  return (
    <Box sx={{ p: 3 }}>
      <StyledPaper>
        <Box sx={{ 
          p: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Typography variant="h6" sx={{ 
            color: 'white',
            fontWeight: 500,
            background: 'linear-gradient(90deg, #2962FF 0%, #34D399 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Payment Requests
          </Typography>
          <GradientButton
            variant="contained"
            onClick={() => {/* Handle new request */}}
            startIcon={<AccountBalance />}
          >
            New Request
          </GradientButton>
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell>Request Date</StyledTableHeaderCell>
              {fundId === 'all' && (
                <StyledTableHeaderCell>Fund</StyledTableHeaderCell>
              )}
              <StyledTableHeaderCell>Vendor</StyledTableHeaderCell>
              <StyledTableHeaderCell align="right">Amount</StyledTableHeaderCell>
              <StyledTableHeaderCell>Category</StyledTableHeaderCell>
              <StyledTableHeaderCell>Status</StyledTableHeaderCell>
              <StyledTableHeaderCell>Compliance</StyledTableHeaderCell>
              <StyledTableHeaderCell align="center">Actions</StyledTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <StyledTableRow key={request.id}>
                <StyledTableCell>{formatDate(request.requestDate)}</StyledTableCell>
                {fundId === 'all' && (
                  <StyledTableCell>
                    <Tooltip title={request.fundName}>
                      <Typography noWrap sx={{ maxWidth: 150 }}>
                        {request.fundName}
                      </Typography>
                    </Tooltip>
                  </StyledTableCell>
                )}
                <StyledTableCell>
                  <Box display="flex" alignItems="center">
                    {request.vendor.name}
                    {request.vendor.ofacStatus === 'blocked' && (
                      <Tooltip title="OFAC Warning">
                        <Cancel color="error" sx={{ ml: 1 }} />
                      </Tooltip>
                    )}
                  </Box>
                </StyledTableCell>
                <StyledTableCell align="right" sx={{
                  color: request.amount < 0 ? '#ff8a80' : '#69f0ae'
                }}>
                  {formatCurrency(request.amount)}
                </StyledTableCell>
                <StyledTableCell>{request.category}</StyledTableCell>
                <StyledTableCell>
                  <Chip 
                    size="small" 
                    label={getStatusChip(request.status).label}
                    sx={{
                      background: 'linear-gradient(90deg, rgba(41,98,255,0.1) 0%, rgba(52,211,153,0.1) 100%)',
                      border: '1px solid rgba(82,153,255,0.2)',
                      color: 'white'
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  {request.compliance.checks.some(c => c.status === 'fail') ? (
                    <Tooltip title="Compliance issues detected">
                      <Cancel sx={{ color: '#ff8a80' }} />
                    </Tooltip>
                  ) : (
                    <CheckCircle sx={{ color: '#69f0ae' }} />
                  )}
                </StyledTableCell>
                <StyledTableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedRequest(request);
                      setDetailsOpen(true);
                    }}
                    sx={{ 
                      color: 'rgba(255,255,255,0.7)',
                      '&:hover': {
                        color: '#2962FF'
                      }
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </StyledPaper>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedRequest && (
          <>
            <DialogTitle>
              Payment Request Details - {selectedRequest.vendor.name}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Amount: {formatCurrency(selectedRequest.amount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Category: {selectedRequest.category}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Compliance Information */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Compliance Status
                  </Typography>
                  {selectedRequest.compliance.checks.map((check, index) => (
                    <Alert
                      key={index}
                      severity={check.status === 'pass' ? 'success' : check.status === 'warning' ? 'warning' : 'error'}
                      sx={{ mb: 1 }}
                    >
                      {check.message}
                    </Alert>
                  ))}
                </Grid>

                {/* Documents */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Supporting Documents
                  </Typography>
                  {selectedRequest.documents.map((doc) => (
                    <Chip
                      key={doc.id}
                      label={doc.name}
                      onClick={() => {
                        setSelectedDocument(doc);
                        setDocumentViewerOpen(true);
                      }}
                      icon={<AttachFile />}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Grid>

                {/* Budget Information */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Budget Information
                  </Typography>
                  <Alert severity={selectedRequest.amount > selectedRequest.budget.remaining ? 'error' : 'info'}>
                    Budget Line: {selectedRequest.budget.lineItem}
                    <br />
                    Remaining: {formatCurrency(selectedRequest.budget.remaining)}
                    <br />
                    Total Allocated: {formatCurrency(selectedRequest.budget.totalAllocated)}
                  </Alert>
                </Grid>

                {/* Approval History */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Approval History
                  </Typography>
                  {selectedRequest.approvals.map((approval, index) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        {approval.level} - {approval.approver}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatDate(approval.date)} - {approval.notes}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setInfoDialogOpen(true)}
                startIcon={<Help />}
              >
                Request Info
              </Button>
              <Button
                onClick={() => setRejectDialogOpen(true)}
                startIcon={<Cancel />}
                color="error"
              >
                Reject
              </Button>
              <Button
                onClick={() => handleApprove(selectedRequest)}
                startIcon={<CheckCircle />}
                color="success"
                disabled={selectedRequest.compliance.checks.some(c => c.status === 'fail')}
              >
                Approve
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
      >
        <DialogTitle>Reject Payment Request</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Rejection"
            fullWidth
            multiline
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error">
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
      >
        <DialogTitle>Request Additional Information</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Information Needed"
            fullWidth
            multiline
            rows={4}
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRequestInfo} color="primary">
            Send Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Viewer */}
      <DocumentViewer
        open={documentViewerOpen}
        document={selectedDocument}
        onClose={() => setDocumentViewerOpen(false)}
      />
    </Box>
  );
}; 